import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navigation } from './Navigation';
import { SitesMap } from './SitesMap';
import { ObservationsCard } from './ObservationsCard';
import { WorkersCard } from './WorkersCard';
import { StatCard } from './StatCard';
import { InspectionsModal } from './InspectionsModal';
import { IssuesModal } from './IssuesModal';
import { FilterPills } from './FilterPills';
import { FilterBanner } from './FilterBanner';
import { SitesOverviewTable } from './SitesOverviewTable';
import { AnimatedNumber } from './AnimatedNumber';
import { BiltonAIWidget } from './BiltonAIWidget';
import { RiskAreasWidget } from './RiskAreasWidget';
import { PreventionView } from './prevention/PreventionView';
import { SafetyScoreCard } from './SafetyScoreCard';
import { useProjectFilter, AVAILABLE_PROJECTS } from '../contexts/ProjectFilterContext';
import { useContractorFilter, AVAILABLE_CONTRACTORS } from '../contexts/ContractorFilterContext';
import { ProjectSelector } from './ProjectSelector';
import { ContractorSelector } from './ContractorSelector';
import { useDashboardData } from '../hooks/useDashboardData';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { CheckCircle2, AlertCircle, AlertTriangle, FileText, Maximize2, Minimize2, Download, ChevronDown, X, Eye, ClipboardList, Building2, Settings2, LayoutGrid, Layers, Columns, ArrowRight, Search, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from './ui/Card';
import { motion, AnimatePresence } from 'framer-motion';
import { useScreenInit } from '../useScreenInit.js';
type IssueType = 'Documents' | 'Inspections' | 'Pre-Shifts' | 'Toolbox Talk' | 'JHA' | 'PTP' | 'all';
type LayoutVariant = 'O' | 'A' | 'B' | 'C' | 'D';
type SectionType = 'overview' | 'compliance' | 'sites' | 'prevention';
export function Dashboard() {
  // Modals & Filters State
  const [isInspectionsModalOpen, setIsInspectionsModalOpen] = useState(false);
  const [isIssuesModalOpen, setIsIssuesModalOpen] = useState(false);
  const [issuesFilterType, setIssuesFilterType] = useState<IssueType>('all');
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  // Layout State
  const [layoutVariant, setLayoutVariant] = useState<LayoutVariant>(() => {
    const saved = localStorage.getItem('dashboard-layout-variant');
    return saved as LayoutVariant || 'B';
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const screenInit = useScreenInit();
  const tabFromUrl = searchParams.get('tab') as SectionType | null;
  const [activeTab, setActiveTab] = useState<SectionType>(screenInit.tab as SectionType || tabFromUrl || 'prevention');
  // Sync activeTab with URL search params
  useEffect(() => {
    const t = searchParams.get('tab') as SectionType | null;
    if (t && (t === 'compliance' || t === 'prevention')) {
      setActiveTab(t);
    }
  }, [searchParams]);
  const [expandedCard, setExpandedCard] = useState<'compliance' | 'sites' | null>(null); // For Variant D
  const [activeSection, setActiveSection] = useState<SectionType>('overview'); // For Variant A
  const [showSwitcher, setShowSwitcher] = useState(false);
  // Escape key to close expanded map
  useEscapeKey(() => setIsMapExpanded(false), isMapExpanded);
  const handleSetLayoutVariant = (v: LayoutVariant) => {
    setLayoutVariant(v);
    localStorage.setItem('dashboard-layout-variant', v);
  };
  // Refs for ScrollSpy (Variant A & C)
  const overviewRef = useRef<HTMLDivElement>(null);
  const complianceRef = useRef<HTMLDivElement>(null);
  const sitesRef = useRef<HTMLDivElement>(null);
  const {
    selectedProjects
  } = useProjectFilter();
  const {
    selectedContractors
  } = useContractorFilter();
  const data = useDashboardData();
  // ScrollSpy Logic
  useEffect(() => {
    if (layoutVariant !== 'A' && layoutVariant !== 'C') return;
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id as SectionType);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    if (overviewRef.current) observer.observe(overviewRef.current);
    if (complianceRef.current) observer.observe(complianceRef.current);
    if (sitesRef.current) observer.observe(sitesRef.current);
    return () => observer.disconnect();
  }, [layoutVariant]);
  const scrollToSection = (sectionId: SectionType) => {
    const refs = {
      overview: overviewRef,
      compliance: complianceRef,
      sites: sitesRef
    };
    const ref = refs[sectionId];
    if (ref.current) {
      const yOffset = -140; // Adjust for sticky header
      const y = ref.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // Data Calculations
  const allProjectsSelected = selectedProjects.length === AVAILABLE_PROJECTS.length;
  const allContractorsSelected = selectedContractors.length === AVAILABLE_CONTRACTORS.length;
  const hasActiveFilters = !allProjectsSelected || !allContractorsSelected;
  const totalDocuments = data.documents.valid + data.documents.expired + data.documents.expiringSoon;
  const totalIssues = data.documents.expired + data.inspections.weekIncomplete + data.preShifts.todayIncomplete + data.toolboxTalk.thisWeekIncomplete + data.jha.thisWeekIncomplete + data.ptp.todayIncomplete;
  const riskScore = 57; // Hardcoded for now, matches SafetyScoreCard
  const totalSites = selectedProjects.length;
  const isSiteFiltered = !allProjectsSelected;
  const isContractorFiltered = !allContractorsSelected;
  const contractorOnlyNote = isContractorFiltered ? 'All sites · Not contractor-specific' : undefined;
  const getDisplayText = () => {
    const parts = [];
    if (!allProjectsSelected) {
      parts.push(selectedProjects.length === 1 ? selectedProjects[0] : `${selectedProjects.length} Sites`);
    } else {
      parts.push('All Sites');
    }
    if (!allContractorsSelected) {
      parts.push(selectedContractors.length === 1 ? selectedContractors[0] : `${selectedContractors.length} Contractors`);
    } else {
      parts.push('All Contractors');
    }
    return parts.join(' · ');
  };
  const openIssuesModal = (type: IssueType) => {
    setIssuesFilterType(type);
    setIsIssuesModalOpen(true);
  };
  // --- RENDER HELPERS FOR CONTENT SECTIONS ---
  const renderOverview = () => <div className="space-y-6" data-id="element-101" data-mp="1nta2lwj6avoc">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[480px]" data-id="element-102" data-mp="1ntiilxaleh3r">
        <Card className="h-full flex flex-col p-0 relative overflow-hidden" noPadding data-id="element-103" data-mp="1iufce9ivm4u7">
          <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded shadow-sm" data-id="element-104" data-mp="1rbzhyv2ek6vh">
            <h2 className="font-bold text-gray-900" data-id="element-105" data-mp="1vbq3aj95tusm">Sites map</h2>
          </div>
          <button onClick={() => setIsMapExpanded(true)} className="absolute top-4 right-4 z-10 bg-white p-2 rounded shadow-sm hover:bg-gray-50 text-gray-600 transition-colors" title="Expand map" data-id="element-106" data-mp="202zsjiqsqw3z">
            <Maximize2 size={18} data-id="element-107" data-mp="18e4z7pbh2gzw" />
          </button>
          <SitesMap data-id="element-108" data-mp="1cddtqwec6iwi" />
        </Card>
        <div className="min-h-[280px] h-full" data-id="element-109" data-mp="1cefg3x0syrtj">
          <BiltonAIWidget data-id="element-110" data-mp="1hd9wytf7m6b0" />
        </div>
      </div>
    </div>;
  const renderCompliance = () => <div className="space-y-6" data-id="element-111" data-mp="1mbtln1l6tw72">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-112" data-mp="1md4lwrffcltf">
        <SafetyScoreCard data-id="element-113" data-mp="2ssfe32g387e5" />
        <WorkersCard data={data.workers} isFiltered={hasActiveFilters} data-id="element-114" data-mp="2ssyntvvl3ph3" />
        <ObservationsCard data={data.observations} isFiltered={hasActiveFilters} data-id="element-115" data-mp="2xr0283i2f0m8" />
      </div>

      <motion.div key={`row1-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-116" data-mp="3iuj3q64qrk16">
        <StatCard title="Documents" count={totalDocuments} actionLabel="View all" actionLink="/documents" onSolveIssues={() => openIssuesModal('Documents')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-117" data-mp="3nt2ow4bigrsh">
          <div className="grid grid-cols-3 gap-4 h-full items-center pt-2" data-id="element-118" data-mp="fe28w8pjp9l4">
            <Link to="/documents?status=valid" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-119" data-mp="agk855lom0o7">
              <div className="flex items-center justify-center gap-1.5 text-emerald-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-120" data-mp="2c8oxqafpklvr">
                <CheckCircle2 size={18} data-id="element-121" data-mp="wnmt88g5khqk" />
                <AnimatedNumber value={data.documents.valid} className="text-2xl font-bold" data-id="element-122" data-mp="wneiam3lbmi0" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-emerald-600 transition-colors" data-id="element-123" data-mp="mxl4rfjw0xb4">
                Valid
              </span>
            </Link>
            <Link to="/documents?status=expired" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-124" data-mp="90oilreps40k">
              <div className="flex items-center justify-center gap-1.5 text-red-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-125" data-mp="3ypfmimvz945">
                <AlertCircle size={18} data-id="element-126" data-mp="1uc6fdueayifg" />
                <AnimatedNumber value={data.documents.expired} className="text-2xl font-bold" data-id="element-127" data-mp="1yd9u2dkm68f3" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-red-600 transition-colors" data-id="element-128" data-mp="12sndtqyzwjdt">
                Expired
              </span>
            </Link>
            <Link to="/documents?status=expiring-soon" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-129" data-mp="swk1jsafj4d5">
              <div className="flex items-center justify-center gap-1.5 text-amber-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-130" data-mp="1lk6evs9glnd7">
                <AlertTriangle size={18} data-id="element-131" data-mp="1qirh1x89uh4a" />
                <AnimatedNumber value={data.documents.expiringSoon} className="text-2xl font-bold" data-id="element-132" data-mp="1ck1ied5t09dt" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-amber-600 transition-colors" data-id="element-133" data-mp="1gnfzl75havsu">
                Expiring soon
              </span>
            </Link>
          </div>
        </StatCard>

        <StatCard title="Inspections & Logs" actionLabel="View all" actionLink="/inspections" onQuickView={() => setIsInspectionsModalOpen(true)} onSolveIssues={() => openIssuesModal('Inspections')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-134" data-mp="53v51lex7z0j">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-135" data-mp="2li9axgkj0u3">
            <Link to="/inspections?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-136" data-mp="3b1bqzfmmyicq">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-137" data-mp="3ksg2u6h0ln3c">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-138" data-mp="2sb3ql5abm608">
                {data.inspections.todayTotal === 0 ? <>
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-139" data-mp="2nbhex0a54xtj" />
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-140" data-mp="3d0xxfvdwhhxd" />
                  </> : <>
                    <AnimatedNumber value={data.inspections.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-141" data-mp="3gzx17cnwb8n8" />
                    <span className="text-lg text-gray-400" data-id="element-142" data-mp="2u1a9zfvx3hwf">
                      /<AnimatedNumber value={data.inspections.todayTotal} data-id="element-143" data-mp="2u2v6hb1slawx" />
                    </span>
                  </>}
              </div>
              <span className="text-xs text-gray-500" data-id="element-144" data-mp="331fd3du2vu4s">
                {data.inspections.todayTotal === 0 ? 'No inspections' : 'Incomplete'}
              </span>
            </Link>
            <Link to="/inspections?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-145" data-mp="iaog6cdxylkv">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-146" data-mp="27rk0c6miizc2">
                Earlier this week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-147" data-mp="1y2jv45hf37pf">
                <AnimatedNumber value={data.inspections.weekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-148" data-mp="1t3yui4y96m0g" />
                <span className="text-lg text-gray-400" data-id="element-149" data-mp="1k3kbxdcobl0n">
                  /<AnimatedNumber value={data.inspections.weekTotal} data-id="element-150" data-mp="1k1zgzmmg6029" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-151" data-mp="1f60w490bv06l">Incomplete</span>
            </Link>
          </div>
        </StatCard>

        <StatCard title="Pre-Shifts" actionLabel="View all" actionLink="/pre-shift" onSolveIssues={() => openIssuesModal('Pre-Shifts')} moreMenuItems={[{
        label: 'Download Pre-Shift report',
        icon: <Download size={16} data-id="element-153" data-mp="3chzah849nkuv" />,
        onClick: () => {}
      }]} isFiltered={hasActiveFilters} data-id="element-152" data-mp="39hys399mlzkj">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-154" data-mp="32mfhoul012ac">
            <Link to="/pre-shift?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-155" data-mp="37kgyf8uycpin">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-156" data-mp="2yku7jgzun23u">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-157" data-mp="2otzjq86yfuga">
                {data.preShifts.todayTotal === 0 ? <>
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-158" data-mp="2auzuaybmbbj2" />
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-159" data-mp="2ewbf8qjjr8kx" />
                  </> : <>
                    <AnimatedNumber value={data.preShifts.todayIncomplete} className="text-2xl font-bold text-red-500" data-id="element-160" data-mp="1w815lb45av81" />
                    <span className="text-lg text-gray-400" data-id="element-161" data-mp="25a0ok8yq09dm">
                      /<AnimatedNumber value={data.preShifts.todayTotal} data-id="element-162" data-mp="208uti4hl8a0x" />
                    </span>
                  </>}
              </div>
              <span className="text-xs text-gray-500" data-id="element-163" data-mp="2f4is9e4504xf">
                {data.preShifts.todayTotal === 0 ? 'No pre-log' : 'Incomplete'}
              </span>
            </Link>
            <Link to="/pre-shift?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-164" data-mp="2nxnkrnn9ltp7">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-165" data-mp="2xxmtplnljm2l">
                Earlier this week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-166" data-mp="1qlf211kpngnv">
                <AnimatedNumber value={data.preShifts.weekIncomplete} className="text-2xl font-bold text-red-500" data-id="element-167" data-mp="1ulxrioz4samw" />
                <span className="text-lg text-gray-400" data-id="element-168" data-mp="1zj7hc5qfmob9">
                  /<AnimatedNumber value={data.preShifts.weekTotal} data-id="element-169" data-mp="24d26hoot3vhq" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-170" data-mp="29abur10gm13z">Incomplete</span>
            </Link>
          </div>
        </StatCard>
      </motion.div>

      <motion.div key={`row2-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3,
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-171" data-mp="2ec148c81a6py">
        <StatCard title="Toolbox Talk" actionLabel="View all" actionLink="/toolbox-talk" onSolveIssues={() => openIssuesModal('Toolbox Talk')} isFiltered={hasActiveFilters} data-id="element-172" data-mp="sxn1pyqcvzc2">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-173" data-mp="gkjdbfd6w9u9">
            <Link to="/toolbox-talk?period=thisweek&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-174" data-mp="bo2jq5n8139s">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-175" data-mp="zd3wu67012le">
                This week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-176" data-mp="uc7kr5chua4w">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-177" data-mp="qbdzykuclbg8" />
                <AnimatedNumber value={data.toolboxTalk.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-178" data-mp="3b2o6mfpze233" />
                <span className="text-lg text-gray-400" data-id="element-179" data-mp="1j1mtrxku76ik">
                  /<AnimatedNumber value={data.toolboxTalk.thisWeekTotal} data-id="element-180" data-mp="1iz0n005rsv1e" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-181" data-mp="1a0f7yeziby5d">Incomplete</span>
            </Link>
            <Link to="/toolbox-talk?period=previousweek&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-182" data-mp="1a29svtas3q9g">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-183" data-mp="1o0g6vow7ovi7">
                Previous week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-184" data-mp="1sxgazu9i3o6o">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-185" data-mp="2go3qb0lqxg39" />
                <AnimatedNumber value={data.toolboxTalk.previousWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-186" data-mp="2uu5p9x2cs7pp" />
                <span className="text-lg text-gray-400" data-id="element-187" data-mp="34kgsfhe7qcvj">
                  /<AnimatedNumber value={data.toolboxTalk.previousWeekTotal} data-id="element-188" data-mp="34m1lt3osjprt" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-189" data-mp="2bv9x0ra223ei">Incomplete</span>
            </Link>
          </div>
        </StatCard>

        <StatCard title="JHA" actionLabel="View all" actionLink="/jha" onSolveIssues={() => openIssuesModal('JHA')} isFiltered={hasActiveFilters} data-id="element-190" data-mp="27uzpvbaj16y2">
          <div className="flex items-center justify-center h-full pt-2" data-id="element-191" data-mp="2gtuval5qyd0b">
            <Link to="/jha?period=entireproject&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-4 transition-colors cursor-pointer group w-full max-w-[200px]" data-id="element-192" data-mp="12yuq2s5mm8u">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-193" data-mp="w9wwgjvfrppa">
                Entire project(s)
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-194" data-mp="15abjporynevf">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-195" data-mp="11883yny9do4c" />
                <AnimatedNumber value={data.jha.entireProjectsIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-196" data-mp="1f6eutk4sinui" />
                <span className="text-lg text-gray-400" data-id="element-197" data-mp="1ozvn2jpa3uw2">
                  /<AnimatedNumber value={data.jha.entireProjectsTotal} data-id="element-198" data-mp="1jzr5eqcmwu49" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-199" data-mp="1xz8znl8p8zl0">Incomplete</span>
            </Link>
          </div>
        </StatCard>

        <StatCard title="PTP" actionLabel="View all" actionLink="/ptp" onSolveIssues={() => openIssuesModal('PTP')} isFiltered={hasActiveFilters} data-id="element-200" data-mp="1sxb7u2m8gwgg">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-201" data-mp="2hi6fh3svxc05">
            <Link to="/ptp?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-202" data-mp="23jybwd1cfcg0">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-203" data-mp="1tsvbldg8n1hs">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-204" data-mp="1jxtnmkxp85gs">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-205" data-mp="1outvn1e3ziaj" />
                <AnimatedNumber value={data.ptp.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-206" data-mp="1ay6lty124xn7" />
                <span className="text-lg text-gray-400" data-id="element-207" data-mp="3hlxlj05rr5hp">
                  /<AnimatedNumber value={data.ptp.todayTotal} data-id="element-208" data-mp="3cskwwq13ayoy" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-209" data-mp="37ut31qgvx2i4">Incomplete</span>
            </Link>
            <Link to="/ptp?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-210" data-mp="33tzf4x3fznp8">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-211" data-mp="3vl5kbry0sbld">
                Last 7 days
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-212" data-mp="3mjp9jj3am7gm">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-213" data-mp="3mlil9csrjhvh" />
                <AnimatedNumber value={data.ptp.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-214" data-mp="2b37pyjksnzbe" />
                <span className="text-lg text-gray-400" data-id="element-215" data-mp="vlkryw0ujvw0">
                  /<AnimatedNumber value={data.ptp.thisWeekTotal} data-id="element-216" data-mp="zm4qo412j7k9" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-217" data-mp="14dmq2nng1034">Incomplete</span>
            </Link>
          </div>
        </StatCard>
      </motion.div>
    </div>;
  const renderSites = () => <SitesOverviewTable isFiltered={hasActiveFilters} isProjectFiltered={!allProjectsSelected} isContractorFiltered={!allContractorsSelected} selectedSites={selectedProjects} data-id="element-218" data-mp="1eb18utwua0yl" />;
  // --- ORIGINAL LAYOUT (no sections) ---
  const renderOriginal = () => <div className="space-y-6" data-id="element-219" data-mp="3l20fav59w0nm">
      {/* Row 1: Map | Risk Areas | BiltonAI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[480px]" data-id="element-220" data-mp="3l43adtla8jz7">
        <Card className="h-full flex flex-col p-0 relative overflow-hidden" noPadding data-id="element-221" data-mp="3p440ibhqujm3">
          <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded shadow-sm" data-id="element-222" data-mp="2tvibdrmoxyff">
            <h2 className="font-bold text-gray-900" data-id="element-223" data-mp="2pu6hvb0p4t1y">Sites map</h2>
          </div>
          <button onClick={() => setIsMapExpanded(true)} className="absolute top-4 right-4 z-10 bg-white p-2 rounded shadow-sm hover:bg-gray-50 text-gray-600 transition-colors" title="Expand map" data-id="element-224" data-mp="33qtrad9u5bor">
            <Maximize2 size={18} data-id="element-225" data-mp="264cxhi0b9g7r" />
          </button>
          <SitesMap data-id="element-226" data-mp="265o03ei0shxa" />
        </Card>
        <div className="min-h-[280px] h-full" data-id="element-227" data-mp="2ksh7zyp7mlfg">
          <RiskAreasWidget data-id="element-228" data-mp="2kubtbe4e8fk2" />
        </div>
        <div className="min-h-[280px] h-full" data-id="element-229" data-mp="2g20u6dhh8xsk">
          <BiltonAIWidget data-id="element-230" data-mp="2g18xt0g22w18" />
        </div>
      </div>

      {/* Row 2: Safety Score | Workers | Observations */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-231" data-mp="1savb7pg8ppe1">
        <SafetyScoreCard data-id="element-232" data-mp="1ne507gjaljzh" />
        <WorkersCard data={data.workers} isFiltered={hasActiveFilters} data-id="element-233" data-mp="1nctw1flxqa7u" />
        <ObservationsCard data={data.observations} isFiltered={hasActiveFilters} data-id="element-234" data-mp="2v2iq5ctfi3t" />
      </div>

      {/* Stats Row 1: Documents, Inspections, Pre-Shifts */}
      <motion.div key={`orig-r1-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-235" data-mp="3q0k39t6pq6ca">
        <StatCard title="Documents" count={totalDocuments} actionLabel="View all" actionLink="/documents" onSolveIssues={() => openIssuesModal('Documents')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-236" data-mp="qjlvax2weh1p">
          <div className="grid grid-cols-3 gap-4 h-full items-center pt-2" data-id="element-237" data-mp="2xajxls4e0p8d">
            <Link to="/documents?status=valid" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-238" data-mp="32apq1aihefri">
              <div className="flex items-center justify-center gap-1.5 text-emerald-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-239" data-mp="12vo5x47nz5y3">
                <CheckCircle2 size={18} data-id="element-240" data-mp="u2sxk1mwgu43" />
                <AnimatedNumber value={data.documents.valid} className="text-2xl font-bold" data-id="element-241" data-mp="u1rjrpei1tio" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-emerald-600 transition-colors" data-id="element-242" data-mp="k4wnze9m2783">
                Valid
              </span>
            </Link>
            <Link to="/documents?status=expired" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-243" data-mp="f5tsakfitdcs">
              <div className="flex items-center justify-center gap-1.5 text-red-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-244" data-mp="25i98127y0pu8">
                <AlertCircle size={18} data-id="element-245" data-mp="3nejqft6p00fx" />
                <AnimatedNumber value={data.documents.expired} className="text-2xl font-bold" data-id="element-246" data-mp="3ndi956z32hpr" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-red-600 transition-colors" data-id="element-247" data-mp="11bbxl81l8va">
                Expired
              </span>
            </Link>
            <Link to="/documents?status=expiring-soon" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-248" data-mp="34h0eagoxu1pa">
              <div className="flex items-center justify-center gap-1.5 text-amber-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-249" data-mp="3dhot3dm5o638">
                <AlertTriangle size={18} data-id="element-250" data-mp="3ihb37e6ot67t" />
                <AnimatedNumber value={data.documents.expiringSoon} className="text-2xl font-bold" data-id="element-251" data-mp="sr7ssilkk2xz" />
              </div>
              <span className="text-xs text-gray-500 group-hover:text-amber-600 transition-colors" data-id="element-252" data-mp="xog1lsktuumk">
                Expiring soon
              </span>
            </Link>
          </div>
        </StatCard>
        <StatCard title="Inspections & Logs" actionLabel="View all" actionLink="/inspections" onQuickView={() => setIsInspectionsModalOpen(true)} onSolveIssues={() => openIssuesModal('Inspections')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-253" data-mp="4ggvj2bwnzz2">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-254" data-mp="n5don2vkgmfp">
            <Link to="/inspections?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-255" data-mp="n7hqzgdthddv">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-256" data-mp="32vhusdraslih">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-257" data-mp="2yx0x0ktrondq">
                {data.inspections.todayTotal === 0 ? <>
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-258" data-mp="2sr9vbm95grum" />
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-259" data-mp="2f1g3cv2clrgo" />
                  </> : <>
                    <AnimatedNumber value={data.inspections.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-260" data-mp="2jygal9axp09d" />
                    <span className="text-lg text-gray-400" data-id="element-261" data-mp="29zgzlk2p4sj7">
                      /<AnimatedNumber value={data.inspections.todayTotal} data-id="element-262" data-mp="1w3djeuonqkpq" />
                    </span>
                  </>}
              </div>
              <span className="text-xs text-gray-500" data-id="element-263" data-mp="3qgcwag1nl0tb">
                {data.inspections.todayTotal === 0 ? 'No inspections' : 'Incomplete'}
              </span>
            </Link>
            <Link to="/inspections?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-264" data-mp="1127r8cdw09gw">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-265" data-mp="1aykvg47265pw">
                Earlier this week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-266" data-mp="1js6a7nj83mzb">
                <AnimatedNumber value={data.inspections.weekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-267" data-mp="1juack11h4dxh" />
                <span className="text-lg text-gray-400" data-id="element-268" data-mp="1yp6qmuvyrvih">
                  /<AnimatedNumber value={data.inspections.weekTotal} data-id="element-269" data-mp="1yooojhmmen2n" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-270" data-mp="3vjba9ahhlj92">Incomplete</span>
            </Link>
          </div>
        </StatCard>
        <StatCard title="Pre-Shifts" actionLabel="View all" actionLink="/pre-shift" onSolveIssues={() => openIssuesModal('Pre-Shifts')} moreMenuItems={[{
        label: 'Download Pre-Shift report',
        icon: <Download size={16} data-id="element-272" data-mp="28tdiu7bvfhjr" />,
        onClick: () => {}
      }]} isFiltered={hasActiveFilters} data-id="element-271" data-mp="2hlfd1nvck1ll">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-273" data-mp="30kta4eodhsgb">
            <Link to="/pre-shift?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-274" data-mp="2wipxhmpywhtg">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-275" data-mp="2mlbgnm04tr0m">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-276" data-mp="1g23jrudn1gfj">
                {data.preShifts.todayTotal === 0 ? <>
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-277" data-mp="1cntvvcgxb1gk" />
                    <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-278" data-mp="1hm54jjvhf5x7" />
                  </> : <>
                    <AnimatedNumber value={data.preShifts.todayIncomplete} className="text-2xl font-bold text-red-500" data-id="element-279" data-mp="13lc82tlme0rg" />
                    <span className="text-lg text-gray-400" data-id="element-280" data-mp="tuznx2hpw9lu">
                      /<AnimatedNumber value={data.preShifts.todayTotal} data-id="element-281" data-mp="ymhgp39l73vq" />
                    </span>
                  </>}
              </div>
              <span className="text-xs text-gray-500" data-id="element-282" data-mp="kwxga43haipl">
                {data.preShifts.todayTotal === 0 ? 'No pre-log' : 'Incomplete'}
              </span>
            </Link>
            <Link to="/pre-shift?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-283" data-mp="ay80qhz21aci">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-284" data-mp="3sgnapqa8syqr">
                Earlier this week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-285" data-mp="696xjai24cqe">
                <AnimatedNumber value={data.preShifts.weekIncomplete} className="text-2xl font-bold text-red-500" data-id="element-286" data-mp="19kkb128awhl" />
                <span className="text-lg text-gray-400" data-id="element-287" data-mp="f7iwq0wzoiug">
                  /<AnimatedNumber value={data.preShifts.weekTotal} data-id="element-288" data-mp="ab0vhb0vb2mv" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-289" data-mp="oy3rtedgf58m">Incomplete</span>
            </Link>
          </div>
        </StatCard>
      </motion.div>

      {/* Stats Row 2: Toolbox Talk, JHA, PTP */}
      <motion.div key={`orig-r2-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
      opacity: 0.8
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3,
      delay: 0.1
    }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-290" data-mp="xz1zq7r0jwwh">
        <StatCard title="Toolbox Talk" actionLabel="View all" actionLink="/toolbox-talk" onSolveIssues={() => openIssuesModal('Toolbox Talk')} isFiltered={hasActiveFilters} data-id="element-291" data-mp="2sdp8am10ten4">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-292" data-mp="24imhw9mkj34j">
            <Link to="/toolbox-talk?period=thisweek&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-293" data-mp="29h68inc3x338">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-294" data-mp="2iix9jevpclqr">
                This week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-295" data-mp="3tx17ew5euu13">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-296" data-mp="3tz43nxx5pje9" />
                <AnimatedNumber value={data.toolboxTalk.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-297" data-mp="1e95dklkinkad" />
                <span className="text-lg text-gray-400" data-id="element-298" data-mp="14hrhknrrx2lz">
                  /<AnimatedNumber value={data.toolboxTalk.thisWeekTotal} data-id="element-299" data-mp="zjpnl7st7ivq" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-300" data-mp="uj1y4qk8ba3m">Incomplete</span>
            </Link>
            <Link to="/toolbox-talk?period=previousweek&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-301" data-mp="qhq50b25c6qo">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-302" data-mp="2kv7w281rflqg">
                Previous week
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-303" data-mp="2z2b6ha3ap1v5">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-304" data-mp="2z085jv0ltobn" />
                <AnimatedNumber value={data.toolboxTalk.previousWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-305" data-mp="2q2o34ir09lzg" />
                <span className="text-lg text-gray-400" data-id="element-306" data-mp="3iqtnv5i27aza">
                  /<AnimatedNumber value={data.toolboxTalk.previousWeekTotal} data-id="element-307" data-mp="3mrx781zbfp59" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-308" data-mp="38r2ux895d41b">Incomplete</span>
            </Link>
          </div>
        </StatCard>
        <StatCard title="JHA" actionLabel="View all" actionLink="/jha" onSolveIssues={() => openIssuesModal('JHA')} isFiltered={hasActiveFilters} data-id="element-309" data-mp="3dp4fxyq4vdff">
          <div className="flex items-center justify-center h-full pt-2" data-id="element-310" data-mp="229ph0ped6bho">
            <Link to="/jha?period=entireproject&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-4 transition-colors cursor-pointer group w-full max-w-[200px]" data-id="element-311" data-mp="1uqpxmjqwkjyb">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-312" data-mp="23qldv2zbf557">
                Entire project(s)
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-313" data-mp="1b4tkof3qga86">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-314" data-mp="173q8c2kw8wbp" />
                <AnimatedNumber value={data.jha.entireProjectsIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-315" data-mp="1g3d9cn9po048" />
                <span className="text-lg text-gray-400" data-id="element-316" data-mp="neze2fa0bhwd">
                  /<AnimatedNumber value={data.jha.entireProjectsTotal} data-id="element-317" data-mp="nefuyv8mengr" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-318" data-mp="2v8mpkigbji0">Incomplete</span>
            </Link>
          </div>
        </StatCard>
        <StatCard title="PTP" actionLabel="View all" actionLink="/ptp" onSolveIssues={() => openIssuesModal('PTP')} isFiltered={hasActiveFilters} data-id="element-319" data-mp="btu1r5opsge1">
          <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-320" data-mp="ls9wdimya7sa">
            <Link to="/ptp?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-321" data-mp="ptbx63wimrwd">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-322" data-mp="zjen1v8cbpqh">
                Today
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-323" data-mp="14g552o3pgvej">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-324" data-mp="19huc7snt4oxc" />
                <AnimatedNumber value={data.ptp.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-325" data-mp="2ozqz10h2ctgu" />
                <span className="text-lg text-gray-400" data-id="element-326" data-mp="2f2w2uo89j55q">
                  /<AnimatedNumber value={data.ptp.todayTotal} data-id="element-327" data-mp="2f4gw8aiuci20" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-328" data-mp="3crpgk273vl2f">Incomplete</span>
            </Link>
            <Link to="/ptp?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-329" data-mp="37sutup60pcd1">
              <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-330" data-mp="2xz4afyoqzs47">
                Last 7 days
              </div>
              <div className="flex items-center justify-center gap-1 mb-1" data-id="element-331" data-mp="3qouhjnryptqt">
                <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-332" data-mp="1pyjb65y800r6" />
                <AnimatedNumber value={data.ptp.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-333" data-mp="1l1j3jqlq2pxy" />
                <span className="text-lg text-gray-400" data-id="element-334" data-mp="177s3d8tvsiew">
                  /<AnimatedNumber value={data.ptp.thisWeekTotal} data-id="element-335" data-mp="179czv3zrabfe" />
                </span>
              </div>
              <span className="text-xs text-gray-500" data-id="element-336" data-mp="127dtrmcmt0er">Incomplete</span>
            </Link>
          </div>
        </StatCard>
      </motion.div>

      {/* Sites Overview Table */}
      <SitesOverviewTable isFiltered={hasActiveFilters} isProjectFiltered={!allProjectsSelected} isContractorFiltered={!allContractorsSelected} selectedSites={selectedProjects} data-id="element-337" data-mp="sbkis7ek9ez3" />
    </div>;
  // --- LAYOUT VARIANTS ---
  const renderVariantA = () => <div className="flex relative" data-id="element-338" data-mp="2v4ht6rvxpab2">
      {/* ScrollSpy Sidebar */}
      <div className="w-12 fixed left-2 top-[200px] bottom-0 flex flex-col items-center py-8 gap-4 bg-transparent z-30" data-id="element-339" data-mp="2z6wah7jyg208">
        <button onClick={() => scrollToSection('overview')} className={`p-2.5 rounded-xl transition-all ${activeSection === 'overview' ? 'bg-blue-600 text-white shadow-lg scale-110' : 'bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600 shadow-sm'}`} title="Overview" data-id="element-340" data-mp="33xl34q7df50y">
          <Eye size={18} data-id="element-341" data-mp="2g91ta68oak6r" />
        </button>
        <button onClick={() => scrollToSection('compliance')} className={`p-2.5 rounded-xl transition-all ${activeSection === 'compliance' ? 'bg-amber-500 text-white shadow-lg scale-110' : 'bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600 shadow-sm'}`} title="Compliance" data-id="element-342" data-mp="2ga3g17z1wv4b">
          <ClipboardList size={18} data-id="element-343" data-mp="5fpjtocvn0on" />
        </button>
        <button onClick={() => scrollToSection('sites')} className={`p-2.5 rounded-xl transition-all ${activeSection === 'sites' ? 'bg-slate-700 text-white shadow-lg scale-110' : 'bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600 shadow-sm'}`} title="Sites at a Glance" data-id="element-344" data-mp="5ee72z1geile">
          <Building2 size={18} data-id="element-345" data-mp="3ltav4ftapuss" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 ml-12 min-w-0 space-y-16" data-id="element-346" data-mp="3guhh8ma3k1s3">
        <div id="overview" ref={overviewRef} className="scroll-mt-[160px]" data-id="element-347" data-mp="2t8193jlwyyig">
          <div className="flex items-center gap-4 mb-6" data-id="element-348" data-mp="2o8exfelqhqbr">
            <div className="w-1.5 h-8 bg-blue-600 rounded-full" data-id="element-349" data-mp="2o5sfcl17vsfa"></div>
            <div data-id="element-350" data-mp="32xkvv0cqzcil">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-351" data-mp="32y40xghau6f7">
                <Eye className="text-blue-600" data-id="element-352" data-mp="2y4hzkkgl5oyp" /> Overview
              </h2>
              <p className="text-sm text-gray-500" data-id="element-353" data-mp="2afgkwfh5thkz">
                High-level risk and AI insights
              </p>
            </div>
          </div>
          {renderOverview()}
        </div>

        <div id="compliance" ref={complianceRef} className="scroll-mt-[160px]" data-id="element-354" data-mp="kz34s41hsmc9">
          <div className="flex items-center gap-4 mb-6" data-id="element-355" data-mp="pptd9q57t5b6">
            <div className="w-1.5 h-8 bg-amber-500 rounded-full" data-id="element-356" data-mp="pre7tfrj4o91"></div>
            <div data-id="element-357" data-mp="b288lrsoyy15">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-358" data-mp="b2rt9g9q80iv">
                <ClipboardList className="text-amber-500" data-id="element-359" data-mp="g11p7t39z55j" /> Compliance
              </h2>
              <p className="text-sm text-gray-500" data-id="element-360" data-mp="13qw0w7buyno7">
                Detailed breakdown of safety logs and documents
              </p>
            </div>
          </div>
          {renderCompliance()}
        </div>

        <div id="sites" ref={sitesRef} className="scroll-mt-[160px]" data-id="element-361" data-mp="uoef1u047x1q">
          <div className="flex items-center gap-4 mb-6" data-id="element-362" data-mp="ypzxduvf53jb">
            <div className="w-1.5 h-8 bg-slate-700 rounded-full" data-id="element-363" data-mp="ysm3rr6kpczy"></div>
            <div data-id="element-364" data-mp="3fdtz864twnie">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-365" data-mp="3fhhsr5a9x9x4">
                <Building2 className="text-slate-700" data-id="element-366" data-mp="3jibgnynpuoq0" /> Sites at a Glance
              </h2>
              <p className="text-sm text-gray-500" data-id="element-367" data-mp="3kbo7p9f7vf4">
                Comprehensive site health and feature adoption
              </p>
            </div>
          </div>
          {renderSites()}
        </div>
      </div>
    </div>;
  const renderVariantB = () => <div className="space-y-2" data-id="element-368" data-mp="3kzqu6xjapgwb">
      {/* Segmented Control */}
      {/* Context Selectors (Only visible on Compliance tab) */}
      {activeTab === 'compliance' && <div className="sticky top-0 z-30 bg-[#fafafa]/80 backdrop-blur-md py-4 -mx-6 px-6 border-b border-gray-200 flex items-center justify-end" data-id="element-369" data-mp="mf8st24abenj">
          <div className="flex items-center gap-3" data-id="element-370" data-mp="ciduokbxbg9s">
            <ProjectSelector data-id="element-371" data-mp="8hu2zwa4d4v1" />
            <ContractorSelector data-id="element-372" data-mp="8ilk622yz01s" />
          </div>
        </div>}

      {/* Tab Content */}
      <AnimatePresence mode="wait" data-id="element-373" data-mp="2x9wjommg84f8">
        <motion.div key={activeTab} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} data-id="element-374" data-mp="2xaza6tfv04um">
          {activeTab === 'overview' && <div className="space-y-6" data-id="element-375" data-mp="1hp5gbydauz7l">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:h-[480px]" data-id="element-376" data-mp="1mhf1z27dtd42">
                <Card className="h-full flex flex-col p-0 relative overflow-hidden" noPadding data-id="element-377" data-mp="1mj9nahmkf78o">
                  <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1 rounded shadow-sm" data-id="element-378" data-mp="1wejpp6gvlgmz">
                    <h2 className="font-bold text-gray-900" data-id="element-379" data-mp="20ihm39etgdcc">Sites map</h2>
                  </div>
                  <button onClick={() => setIsMapExpanded(true)} className="absolute top-4 right-4 z-10 bg-white p-2 rounded shadow-sm hover:bg-gray-50 text-gray-600 transition-colors" title="Expand map" data-id="element-380" data-mp="25eztkh1j6psd">
                    <Maximize2 size={18} data-id="element-381" data-mp="1ee77c2tf8v1u" />
                  </button>
                  <SitesMap data-id="element-382" data-mp="19ecevutzzxeo" />
                </Card>
                <div className="min-h-[280px] h-full" data-id="element-383" data-mp="19fnk7z33dd7w">
                  <BiltonAIWidget data-id="element-384" data-mp="1nedgxdm01avq" />
                </div>
              </div>
            </div>}
          {activeTab === 'compliance' && <div className="space-y-6" data-id="element-385" data-mp="1idfyr10zogu8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-386" data-mp="1xbryxhnkm7q5">
                <SafetyScoreCard data-id="element-387" data-mp="1x9x9bq1ctrfq" />
                <WorkersCard data={data.workers} isFiltered={hasActiveFilters} data-id="element-388" data-mp="1sdgmr09szl4r" />
                <ObservationsCard data={data.observations} isFiltered={hasActiveFilters} data-id="element-389" data-mp="26320nw8qxa5x" />
              </div>

              <motion.div key={`row1-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
            opacity: 0.8
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-390" data-mp="2g1zv1fsurxwu">
                <StatCard title="Documents" count={totalDocuments} actionLabel="View all" actionLink="/documents" onSolveIssues={() => openIssuesModal('Documents')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-391" data-mp="2kq93feo2v1tq">
                  <div className="grid grid-cols-3 gap-4 h-full items-center pt-2" data-id="element-392" data-mp="221c5n0th1r6r">
                    <Link to="/documents?status=valid" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-393" data-mp="26zdy2ccsf2uw">
                      <div className="flex items-center justify-center gap-1.5 text-emerald-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-394" data-mp="1x0ok2uo0i2jx">
                        <CheckCircle2 size={18} data-id="element-395" data-mp="28hcogdj5tt8d" />
                        <AnimatedNumber value={data.documents.valid} className="text-2xl font-bold" data-id="element-396" data-mp="28ins8dclv0zh" />
                      </div>
                      <span className="text-xs text-gray-500 group-hover:text-emerald-600 transition-colors" data-id="element-397" data-mp="1ylsq7kh4iigg">
                        Valid
                      </span>
                    </Link>
                    <Link to="/documents?status=expired" className="text-center border-r border-gray-100 last:border-0 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-398" data-mp="1ppbeog4ukik6">
                      <div className="flex items-center justify-center gap-1.5 text-red-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-399" data-mp="1at2ixok60nci">
                        <AlertCircle size={18} data-id="element-400" data-mp="16umwpmvwrj05" />
                        <AnimatedNumber value={data.documents.expired} className="text-2xl font-bold" data-id="element-401" data-mp="3ignima4d9h44" />
                      </div>
                      <span className="text-xs text-gray-500 group-hover:text-red-600 transition-colors" data-id="element-402" data-mp="3rhlneummpsnr">
                        Expired
                      </span>
                    </Link>
                    <Link to="/documents?status=expiring-soon" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-403" data-mp="2ysxvwhbae71r">
                      <div className="flex items-center justify-center gap-1.5 text-amber-500 mb-1 group-hover:scale-110 transition-transform" data-id="element-404" data-mp="38orfulrc52tk">
                        <AlertTriangle size={18} data-id="element-405" data-mp="2g1onych5zqca" />
                        <AnimatedNumber value={data.documents.expiringSoon} className="text-2xl font-bold" data-id="element-406" data-mp="2g151qjkhefsg" />
                      </div>
                      <span className="text-xs text-gray-500 group-hover:text-amber-600 transition-colors" data-id="element-407" data-mp="1uohr3u67mq9y">
                        Expiring soon
                      </span>
                    </Link>
                  </div>
                </StatCard>

                <StatCard title="Inspections & Logs" actionLabel="View all" actionLink="/inspections" onQuickView={() => setIsInspectionsModalOpen(true)} onSolveIssues={() => openIssuesModal('Inspections')} isFiltered={isSiteFiltered} filterNote={contractorOnlyNote} data-id="element-408" data-mp="1pqq155p4le8e">
                  <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-409" data-mp="1721h0lygvcxo">
                    <Link to="/inspections?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-410" data-mp="t0ywwer18cpe">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-411" data-mp="9ff6soskn6oc">
                        Today
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-412" data-mp="nd3r9viq8bsv">
                        {data.inspections.todayTotal === 0 ? <>
                            <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-413" data-mp="xbt77ir2bm6h" />
                            <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-414" data-mp="sd9k3f0qg4cj" />
                          </> : <>
                            <AnimatedNumber value={data.inspections.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-415" data-mp="124ck0dhxeda8" />
                            <span className="text-lg text-gray-400" data-id="element-416" data-mp="1b2ppafnd4kza">
                              /
                              <AnimatedNumber value={data.inspections.todayTotal} data-id="element-417" data-mp="8p9zxxuejesv" />
                            </span>
                          </>}
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-418" data-mp="mjt3icm30ocr">
                        {data.inspections.todayTotal === 0 ? 'No inspections' : 'Incomplete'}
                      </span>
                    </Link>
                    <Link to="/inspections?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-419" data-mp="11gbhg5lkhmfk">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-420" data-mp="338q7xt26fz7l">
                        Earlier this week
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-421" data-mp="2zqiy6hri2el1">
                        <AnimatedNumber value={data.inspections.weekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-422" data-mp="2uyit6awkovhl" />
                        <span className="text-lg text-gray-400" data-id="element-423" data-mp="2lvztfzo32yzk">
                          /<AnimatedNumber value={data.inspections.weekTotal} data-id="element-424" data-mp="3inadkg3x89jt" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-425" data-mp="3dn5x2q30jetl">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>

                <StatCard title="Pre-Shifts" actionLabel="View all" actionLink="/pre-shift" onSolveIssues={() => openIssuesModal('Pre-Shifts')} moreMenuItems={[{
              label: 'Download Pre-Shift report',
              icon: <Download size={16} data-id="element-427" data-mp="3h40h28gmrzc2" />,
              onClick: () => {}
            }]} isFiltered={hasActiveFilters} data-id="element-426" data-mp="34nivo4aaa90j">
                  <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-428" data-mp="3879m5zdteqj2">
                    <Link to="/pre-shift?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-429" data-mp="3c7jqz8pvfawc">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-430" data-mp="3qwpp0tcz2v2n">
                        Today
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-431" data-mp="2fh12g33mry2d">
                        {data.preShifts.todayTotal === 0 ? <>
                            <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-432" data-mp="2kdhpsv30a8ee" />
                            <div className="w-3 h-3 rounded-full bg-gray-300" data-id="element-433" data-mp="2bx0m1cicbt7t" />
                          </> : <>
                            <AnimatedNumber value={data.preShifts.todayIncomplete} className="text-2xl font-bold text-red-500" data-id="element-434" data-mp="2lvyh6ya9ukzs" />
                            <span className="text-lg text-gray-400" data-id="element-435" data-mp="1t8vm6g4t0sea">
                              /
                              <AnimatedNumber value={data.preShifts.todayTotal} data-id="element-436" data-mp="1oau5u2yrj76i" />
                            </span>
                          </>}
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-437" data-mp="1ahb5ochy4ah8">
                        {data.preShifts.todayTotal === 0 ? 'No pre-log' : 'Incomplete'}
                      </span>
                    </Link>
                    <Link to="/pre-shift?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-438" data-mp="35iwtbrrlkkca">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-439" data-mp="3jcxj3w0kmx0i">
                        Earlier this week
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-440" data-mp="2rkydsj1jihv7">
                        <AnimatedNumber value={data.preShifts.weekIncomplete} className="text-2xl font-bold text-red-500" data-id="element-441" data-mp="2vmtf3njh0v8z" />
                        <span className="text-lg text-gray-400" data-id="element-442" data-mp="awm7invbddd4">
                          /<AnimatedNumber value={data.preShifts.weekTotal} data-id="element-443" data-mp="awcib2i3fa8h" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-444" data-mp="pzm8jffkc288">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>
              </motion.div>

              <motion.div key={`row2-${selectedProjects.join(',')}-${selectedContractors.join(',')}`} initial={{
            opacity: 0.8
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3,
            delay: 0.1
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-445" data-mp="l2m233eswxgl">
                <StatCard title="Toolbox Talk" actionLabel="View all" actionLink="/toolbox-talk" onSolveIssues={() => openIssuesModal('Toolbox Talk')} isFiltered={hasActiveFilters} data-id="element-446" data-mp="3th470w9g3wvz">
                  <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-447" data-mp="1nofiqjf76006">
                    <Link to="/toolbox-talk?period=thisweek&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-448" data-mp="26vw0sya0vxdh">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-449" data-mp="225fl17iqbzny">
                        This week
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-450" data-mp="1naht2frhtc7e">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-451" data-mp="1s8j88plssrdl" />
                        <AnimatedNumber value={data.toolboxTalk.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-452" data-mp="1j6tkbtr4kakk" />
                        <span className="text-lg text-gray-400" data-id="element-453" data-mp="3nnztb2n5ewup">
                          /
                          <AnimatedNumber value={data.toolboxTalk.thisWeekTotal} data-id="element-454" data-mp="3ipokmv8lase2" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-455" data-mp="3rqcy9ou2mqqf">Incomplete</span>
                    </Link>
                    <Link to="/toolbox-talk?period=previousweek&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-456" data-mp="a9cy4s1snobw">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-457" data-mp="j9rnq3lsjpl7">
                        Previous week
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-458" data-mp="2l2fugmxn8v5q">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-459" data-mp="2h1494tlsyw3c" />
                        <AnimatedNumber value={data.toolboxTalk.previousWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-460" data-mp="1jfsyeg86bjew" />
                        <span className="text-lg text-gray-400" data-id="element-461" data-mp="1t6xc7cm44y85">
                          /
                          <AnimatedNumber value={data.toolboxTalk.previousWeekTotal} data-id="element-462" data-mp="15hcjw342kom7" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-463" data-mp="1edtu944m0igw">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>

                <StatCard title="JHA" actionLabel="View all" actionLink="/jha" onSolveIssues={() => openIssuesModal('JHA')} isFiltered={hasActiveFilters} data-id="element-464" data-mp="2pubqp487il3j">
                  <div className="flex items-center justify-center h-full pt-2" data-id="element-465" data-mp="2yc3zsr1yug3c">
                    <Link to="/jha?period=entireproject&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-4 transition-colors cursor-pointer group w-full max-w-[200px]" data-id="element-466" data-mp="2ya11ljqntgnj">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-467" data-mp="3c4tnczx57t2k">
                        Entire project(s)
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-468" data-mp="32cnza1a0hvsn">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-469" data-mp="37c0jee9i0orj" />
                        <AnimatedNumber value={data.jha.entireProjectsIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-470" data-mp="3o8nc8mhoabl" />
                        <span className="text-lg text-gray-400" data-id="element-471" data-mp="8le0guttkbid">
                          /
                          <AnimatedNumber value={data.jha.entireProjectsTotal} data-id="element-472" data-mp="4hg1ql8ep2pu" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-473" data-mp="3qqzbk3w92g3w">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>

                <StatCard title="PTP" actionLabel="View all" actionLink="/ptp" onSolveIssues={() => openIssuesModal('PTP')} isFiltered={hasActiveFilters} data-id="element-474" data-mp="3h04q31qtvkji">
                  <div className="grid grid-cols-2 gap-4 h-full items-center pt-2" data-id="element-475" data-mp="37yyaqvzx0fru">
                    <Link to="/ptp?period=today&status=incomplete" className="text-center border-r border-gray-100 block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-476" data-mp="33151fu3bf5nc">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-477" data-mp="1sg8qjdtlay2v">
                        Today
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-478" data-mp="zs35sr2jd931">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-479" data-mp="zq9tav58run4" />
                        <AnimatedNumber value={data.ptp.todayIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-480" data-mp="18qoja7t5hxwy" />
                        <span className="text-lg text-gray-400" data-id="element-481" data-mp="l2ord4k3s1gs">
                          /<AnimatedNumber value={data.ptp.todayTotal} data-id="element-482" data-mp="l0u6fq8u09cp" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-483" data-mp="3t5endrlyp44t">Incomplete</span>
                    </Link>
                    <Link to="/ptp?period=week&status=incomplete" className="text-center block hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer group" data-id="element-484" data-mp="bowt8j65fube">
                      <div className="text-xs text-gray-900 font-medium mb-2 group-hover:text-blue-600 transition-colors" data-id="element-485" data-mp="kni55vh48b37">
                        Last 7 days
                      </div>
                      <div className="flex items-center justify-center gap-1 mb-1" data-id="element-486" data-mp="ulxy83zpdufc">
                        <FileText size={16} className="text-red-500 group-hover:scale-110 transition-transform" data-id="element-487" data-mp="pnwgpnhxe35z" />
                        <AnimatedNumber value={data.ptp.thisWeekIncomplete} className="text-2xl font-bold text-red-500 group-hover:scale-110 transition-transform" data-id="element-488" data-mp="2rg2ficxr762r" />
                        <span className="text-lg text-gray-400" data-id="element-489" data-mp="1bw1riuxzggmo">
                          /<AnimatedNumber value={data.ptp.thisWeekTotal} data-id="element-490" data-mp="x8grxb0bgzji" />
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-491" data-mp="124xgg6bfhfx4">Incomplete</span>
                    </Link>
                  </div>
                </StatCard>
              </motion.div>

              <SitesOverviewTable isFiltered={hasActiveFilters} isProjectFiltered={!allProjectsSelected} isContractorFiltered={!allContractorsSelected} selectedSites={selectedProjects} data-id="element-492" data-mp="s9dn9ro90vc2" />
            </div>}
          {activeTab === 'prevention' && <PreventionView data-id="element-493" data-mp="28nn8icx723yo" />}
        </motion.div>
      </AnimatePresence>
    </div>;
  const renderVariantC = () => <div className="space-y-0" data-id="element-494" data-mp="2djkr6pvxjmem">
      {/* Overview Section */}
      <div className="pb-12" data-id="element-495" data-mp="2c6co9ckc5h6c">
        <div className="sticky top-[132px] z-30 bg-white/95 backdrop-blur-md shadow-sm border-y border-gray-200 py-3 px-6 -mx-6 mb-6 flex items-center justify-between" data-id="element-496" data-mp="2c2f36le76fjt">
          <div className="flex items-center gap-3" data-id="element-497" data-mp="2273wmqcy0en5">
            <div className="w-1 h-6 bg-blue-600 rounded-full" data-id="element-498" data-mp="1x7r35mrkg9bl"></div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-499" data-mp="1x9lp94ekq7h9">
              <Eye className="text-blue-600" size={20} data-id="element-500" data-mp="2uuhle16hm3w6" /> Overview
            </h2>
          </div>
          <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full" data-id="element-501" data-mp="2pwy936tco16v">
            Risk Score: {riskScore}/100
          </span>
        </div>
        {renderOverview()}
      </div>

      <div className="h-8 bg-gradient-to-b from-transparent to-gray-50/50 -mx-6" data-id="element-502" data-mp="2g4ae8hlxk7as"></div>

      {/* Compliance Section */}
      <div className="pb-12" data-id="element-503" data-mp="38v0rv3vb11w8">
        <div className="sticky top-[132px] z-30 bg-white/95 backdrop-blur-md shadow-sm border-y border-gray-200 py-3 px-6 -mx-6 mb-6 flex items-center justify-between" data-id="element-504" data-mp="38usdf7jj9oix">
          <div className="flex items-center gap-3" data-id="element-505" data-mp="3l59zq6skvqpw">
            <div className="w-1 h-6 bg-amber-500 rounded-full" data-id="element-506" data-mp="3l61mcs476e41"></div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-507" data-mp="3q3tjc0jp8qf3">
              <ClipboardList className="text-amber-500" size={20} data-id="element-508" data-mp="3v36a2wdoyhmy" /> Compliance
            </h2>
          </div>
          <span className="text-sm font-medium text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-100" data-id="element-509" data-mp="3xocnsccfq9j">
            {totalIssues} open issues
          </span>
        </div>
        {renderCompliance()}
      </div>

      <div className="h-8 bg-gradient-to-b from-transparent to-gray-50/50 -mx-6" data-id="element-510" data-mp="cq00r7jzj1mc"></div>

      {/* Sites Section */}
      <div className="pb-12" data-id="element-511" data-mp="hnrpjspc27mb">
        <div className="sticky top-[132px] z-30 bg-white/95 backdrop-blur-md shadow-sm border-y border-gray-200 py-3 px-6 -mx-6 mb-6 flex items-center justify-between" data-id="element-512" data-mp="mnmfnu1aat6b">
          <div className="flex items-center gap-3" data-id="element-513" data-mp="rkmoudtfkc1n">
            <div className="w-1 h-6 bg-slate-700 rounded-full" data-id="element-514" data-mp="1cwwaa6hpw46w"></div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-515" data-mp="17yl0fvrf9too">
              <Building2 className="text-slate-700" size={20} data-id="element-516" data-mp="180xgu4hj7vzm" /> Sites at a
              Glance
            </h2>
          </div>
          <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full" data-id="element-517" data-mp="1gziylxf6j6zc">
            {totalSites} active sites
          </span>
        </div>
        {renderSites()}
      </div>
    </div>;
  const renderVariantD = () => <div className="space-y-8" data-id="element-518" data-mp="p9o83xfl5b93">
      {/* Overview is always visible */}
      <div data-id="element-519" data-mp="130jixf9na2fb">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-520" data-mp="1309rdn8ybn7i">
          <Eye className="text-blue-600" data-id="element-521" data-mp="y7qdep6cqq26" /> Overview
        </h2>
        {renderOverview()}
      </div>

      {/* Expandable Deck */}
      <div className="relative" data-id="element-522" data-mp="ahduykdhdaxc">
        <AnimatePresence mode="wait" data-id="element-523" data-mp="5kdqgdwa4g8c">
          {!expandedCard ? <motion.div key="deck" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-524" data-mp="5i1au7dzuhyg">
              {/* Compliance Preview Card */}
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-t-4 border-t-amber-500" onClick={() => setExpandedCard('compliance')} data-id="element-525" data-mp="2d6dsh0yvdaxv">
                <div className="flex justify-between items-start mb-6" data-id="element-526" data-mp="34w8vu1jkb15j">
                  <div data-id="element-527" data-mp="2qw7lzwlbkjdx">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-id="element-528" data-mp="2qx0ub1zxfgy6">
                      <ClipboardList className="text-amber-500" data-id="element-529" data-mp="2vusns0g7zb4h" /> Compliance
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-530" data-mp="1fgsngodgpjgp">
                      Workers, Observations, and Logs
                    </p>
                  </div>
                  <button className="p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" data-id="element-531" data-mp="1syu19tqfqk4w">
                    <Maximize2 size={18} data-id="element-532" data-mp="1xvu8w92xnuy4" />
                  </button>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 flex items-center justify-between mb-4" data-id="element-533" data-mp="22x9v44guk7i3">
                  <span className="text-amber-800 font-semibold" data-id="element-534" data-mp="27ua34kx9bkbu">
                    Total Open Issues
                  </span>
                  <span className="text-2xl font-bold text-amber-600" data-id="element-535" data-mp="1a9dzl330klmw">
                    {totalIssues}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-536" data-mp="1k57a6gx6a521">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full" data-id="element-537" data-mp="1k3nv2tn9601u">
                    Documents: {data.documents.expired} expired
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full" data-id="element-538" data-mp="rgtbdyy9f4pf">
                    Inspections: {data.inspections.weekIncomplete} missed
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full" data-id="element-539" data-mp="39pwbb8k7tuud">
                    Pre-Shifts: {data.preShifts.todayIncomplete} missed
                  </span>
                </div>
              </Card>

              {/* Sites Preview Card */}
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-t-4 border-t-slate-700" onClick={() => setExpandedCard('sites')} data-id="element-540" data-mp="2vpli3yvfo461">
                <div className="flex justify-between items-start mb-6" data-id="element-541" data-mp="2lzsh1rsx37g1">
                  <div data-id="element-542" data-mp="2hz05grwkoqgl">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-id="element-543" data-mp="2i0b9mstxk088">
                      <Building2 className="text-slate-700" data-id="element-544" data-mp="2d184z9hv3w0s" /> Sites at a Glance
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-545" data-mp="2800xzcnjn0v6">
                      Health scores and feature adoption
                    </p>
                  </div>
                  <button className="p-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" data-id="element-546" data-mp="1ten31cuk9zgy">
                    <Maximize2 size={18} data-id="element-547" data-mp="1ydq990ma2bqi" />
                  </button>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between mb-4" data-id="element-548" data-mp="1ke71wa1aj4f9">
                  <span className="text-slate-800 font-semibold" data-id="element-549" data-mp="1oeqwp6ye5vy8">
                    Active Sites
                  </span>
                  <span className="text-2xl font-bold text-slate-700" data-id="element-550" data-mp="2c5wg1w3jj6da">
                    {totalSites}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-551" data-mp="2h5sl7ynz53uf">
                  <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full border border-emerald-100" data-id="element-552" data-mp="23brsbljpeb1z">
                    All sites healthy
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full" data-id="element-553" data-mp="2v2o2ufiak3m3">
                    High adoption rate
                  </span>
                </div>
              </Card>
            </motion.div> : <motion.div key="expanded" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6" data-id="element-554" data-mp="oddti5mfiiys">
              <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-100" data-id="element-555" data-mp="i19lvnq3i5r">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3" data-id="element-556" data-mp="ffbl350d8o1h">
                  {expandedCard === 'compliance' ? <>
                      <ClipboardList className="text-amber-500" size={28} data-id="element-557" data-mp="ai1u3kxbw4bj" />{' '}
                      Compliance Details
                    </> : <>
                      <Building2 className="text-slate-700" size={28} data-id="element-558" data-mp="1lx70p00kyn0f" /> Sites
                      Overview
                    </>}
                </h2>
                <button onClick={() => setExpandedCard(null)} className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-559" data-mp="dddd5xpsoj8">
                  <Minimize2 size={18} data-id="element-560" data-mp="ecuv51clf1j3" /> Collapse
                </button>
              </div>

              {expandedCard === 'compliance' ? renderCompliance() : renderSites()}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
  return <div className="min-h-screen bg-[#fafafa] flex" data-id="element-561" data-mp="s3rm6nqvfao1">
      <Navigation data-id="element-562" data-mp="s382p2lkoe7w" />

      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden" data-id="element-563" data-mp="s5u8oxstelo0">
        {/* Sticky: Filter Banner + Filter Pills + Solve My Issues Button */}
        <div className={`sticky top-0 z-40 px-6 transition-colors duration-500 ${hasActiveFilters ? 'bg-[#fff3e0]' : 'bg-[#fafafa]'}`} data-id="element-564" data-mp="122yyd7lvcz9k">
          <FilterBanner data-id="element-565" data-mp="17717cm9hhcgx" />
          {!(layoutVariant === 'B' && activeTab === 'prevention') && <div className="py-3" data-id="element-566" data-mp="127y67cwmjqe8">
              <div className="flex items-center justify-between gap-4" data-id="element-567" data-mp="128zkrrcumv0p">
                <FilterPills data-id="element-568" data-mp="y6uu91hp6eia" />
                <button onClick={() => openIssuesModal('all')} className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors shadow-lg whitespace-nowrap" data-id="element-569" data-mp="y7o44bbydk4n">
                  <AlertCircle size={20} data-id="element-570" data-mp="1l6ahb473jacg" />
                  Solve My Issues
                </button>
              </div>
            </div>}
        </div>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden relative" data-id="element-571" data-mp="2dnmymjsndhmf">
          <div className="max-w-[1600px] mx-auto px-6 py-3" data-id="element-572" data-mp="28wemvh3uhk63">
            <div className="flex items-center justify-between mb-2" data-id="element-573" data-mp="28utuny506dbe">
              <div data-id="element-574" data-mp="6ykx3ne1wc3g">
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-1.5" data-id="element-575" data-mp="705udkrr2950">
                  Dashboard
                  {(activeTab === 'prevention' || activeTab === 'compliance') && <>
                      <span className="text-gray-300 font-normal" data-id="element-576" data-mp="27us4b9jeb9a">›</span>
                      <span data-id="element-577" data-mp="261hkkvsz6w0">
                        {activeTab === 'prevention' ? 'Prevention' : 'Safety & Compliance'}
                      </span>
                    </>}
                </h1>
                <p className="text-xs text-gray-500" data-id="element-578" data-mp="3agmk53s39te7">
                  {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-579" data-mp="1dnjf32msjaa3"></div>
            </div>

            {/* Render Selected Layout Variant */}
            <div className="mt-1" data-id="element-580" data-mp="2y5va6nlh1w4p">
              {renderVariantB()}
              <button onClick={() => {
              handleSetLayoutVariant('A');
            }} data-id="element-581" data-mp="2t70kd1p377b3" />
            </div>
          </div>
        </main>

        {/* Modals */}
        <InspectionsModal isOpen={isInspectionsModalOpen} onClose={() => setIsInspectionsModalOpen(false)} data-id="element-582" data-mp="333dnsrafozj1" />
        <IssuesModal isOpen={isIssuesModalOpen} onClose={() => setIsIssuesModalOpen(false)} filterType={issuesFilterType} data-id="element-583" data-mp="3gu0djty7vf2z" />

        {/* Expanded Map Modal */}
        <AnimatePresence data-id="element-584" data-mp="3qt7vyvjt5zwy">
          {isMapExpanded && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setIsMapExpanded(false)} data-id="element-585" data-mp="3lumuksstla6x">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            type: 'spring',
            duration: 0.4,
            bounce: 0.2
          }} className="relative w-full h-full max-w-[95vw] max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-586" data-mp="1fczm0fsa5ir6">
                <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg shadow-sm" data-id="element-587" data-mp="2d19qd1fjgilg">
                  <h2 className="font-bold text-gray-900" data-id="element-588" data-mp="1e7j9gwcohm1">Sites map</h2>
                </div>
                <button onClick={() => setIsMapExpanded(false)} className="absolute top-4 right-4 z-10 bg-white p-2 rounded-lg shadow-sm hover:bg-gray-50 text-gray-600 transition-colors flex items-center gap-2" title="Close expanded map" data-id="element-589" data-mp="3sjph2m1m1azz">
                  <Minimize2 size={18} data-id="element-590" data-mp="3dvl4lzt5hvpn" />
                </button>
                <div className="w-full h-full" data-id="element-591" data-mp="39tr7u2m2tbur">
                  <SitesMap data-id="element-592" data-mp="39syafs90bypa" />
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}